import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MastersService } from '../../../services/masters/masters.service';
import {
  DownloadSection,
  DownloadSubsection,
  DownloadItem,
  GROUPED_DOWNLOADS_CONFIG,
} from './grouped-downloads.config';
import { GroupedDownloadsService } from './grouped-downloads.service';

@Component({
  selector: 'dialog-grouped-downloads',
  templateUrl: './dialog-grouped-downloads.component.html',
  styleUrls: ['./dialog-grouped-downloads.component.scss'],
})
export class DialogGroupedDownloads implements OnInit {
  @Input() title!: string;
  @Input() id!: string;

  @Output() onCloseAction: EventEmitter<string> = new EventEmitter<string>();

  readonly config: DownloadSection[] = GROUPED_DOWNLOADS_CONFIG;
  readonly selectedItemKeys: Set<string> = new Set<string>();
  readonly expandedSections: Record<string, boolean> = {};
  retributiveYear!: string;

  constructor(
    private groupedDownloadsService: GroupedDownloadsService,
    private mastersService: MastersService,
  ) {}

  ngOnInit() {
    this.config.forEach((section) => {
      this.expandedSections[section.id] = true;
    });

    this.mastersService.getRetributiveYear().subscribe((year) => {
      this.retributiveYear = year;
    });
  }

  handleCloseDialog(): void {
    this.onCloseAction.emit(this.id);
  }

  toggleSection(section: DownloadSection): void {
    this.expandedSections[section.id] = !this.expandedSections[section.id];
  }

  isSectionExpanded(section: DownloadSection): boolean {
    return this.expandedSections[section.id];
  }

  onSubsectionToggle(
    section: DownloadSection,
    subsection: DownloadSubsection,
    checked: boolean,
  ): void {
    if (checked && this.getExclusiveSelectedItemKey()) {
      return;
    }

    subsection.items.forEach((item) => {
      if (checked && item.separateDownload) {
        return;
      }

      const key = this.getItemKey(section, subsection, item);
      if (checked) {
        this.selectedItemKeys.add(key);
      } else {
        this.selectedItemKeys.delete(key);
      }
    });
  }

  onItemToggle(
    section: DownloadSection,
    subsection: DownloadSubsection,
    item: DownloadItem,
    checked: boolean,
  ): void {
    const key = this.getItemKey(section, subsection, item);
    if (checked) {
      if (item.separateDownload) {
        this.selectedItemKeys.clear();
        this.selectedItemKeys.add(key);
        return;
      }

      const exclusiveSelectionKey = this.getExclusiveSelectedItemKey();
      if (exclusiveSelectionKey && exclusiveSelectionKey !== key) {
        return;
      }

      this.selectedItemKeys.add(key);
      return;
    }

    this.selectedItemKeys.delete(key);
  }

  isItemSelected(section: DownloadSection, subsection: DownloadSubsection, item: DownloadItem): boolean {
    return this.selectedItemKeys.has(this.getItemKey(section, subsection, item));
  }

  isItemDisabled(section: DownloadSection, subsection: DownloadSubsection, item: DownloadItem): boolean {
    const exclusiveSelectionKey = this.getExclusiveSelectedItemKey();
    if (!exclusiveSelectionKey) {
      return false;
    }

    return this.getItemKey(section, subsection, item) !== exclusiveSelectionKey;
  }

  isSubsectionDisabled(section: DownloadSection, subsection: DownloadSubsection): boolean {
    return !!this.getExclusiveSelectedItemKey();
  }

  isSectionDisabled(section: DownloadSection): boolean {
    return !!this.getExclusiveSelectedItemKey();
  }

  isSubsectionChecked(section: DownloadSection, subsection: DownloadSubsection): boolean {
    const selectableItems = subsection.items.filter((item) => !item.separateDownload);
    if (selectableItems.length === 0) {
      return false;
    }

    return selectableItems.every((item) => this.isItemSelected(section, subsection, item));
  }

  isSubsectionIndeterminate(section: DownloadSection, subsection: DownloadSubsection): boolean {
    const selectedCount = subsection.items.filter((item) =>
      this.isItemSelected(section, subsection, item),
    ).length;

    return selectedCount > 0 && !this.isSubsectionChecked(section, subsection);
  }

  hasSelectedItems(): boolean {
    return this.selectedItemKeys.size > 0;
  }

  getSelectedDownloads(): { origin: string; alias?: string }[] {
    const downloads: { origin: string; alias?: string }[] = [];

    this.config.forEach((section: DownloadSection) => {
      section.subsections.forEach((subsection: DownloadSubsection) => {
        subsection.items.forEach((item: DownloadItem) => {
          if (!this.isItemSelected(section, subsection, item)) {
            return;
          }

          item.origins.forEach((origin: string, index: number) => {
            if (origin) {
              downloads.push({
                origin,
                alias: item.aliases && item.aliases[index] ? item.aliases[index] : undefined,
              });
            }
          });
        });
      });
    });

    return downloads;
  }

  handleDownload(): void {
    if (!this.hasSelectedItems()) {
      return;
    }

    const selectedDownloads = this.getSelectedDownloads();
    const groupedRequest = this.groupedDownloadsService.buildRequest(
      selectedDownloads,
      this.retributiveYear,
    );
    this.groupedDownloadsService.requestDownload(groupedRequest);
  }

  private getItemKey(
    section: DownloadSection,
    subsection: DownloadSubsection,
    item: DownloadItem,
  ): string {
    return `${section.id}::${subsection.id}::${item.id}`;
  }

  private getExclusiveSelectedItemKey(): string | null {
    for (const section of this.config) {
      for (const subsection of section.subsections) {
        for (const item of subsection.items) {
          if (item.separateDownload && this.isItemSelected(section, subsection, item)) {
            return this.getItemKey(section, subsection, item);
          }
        }
      }
    }

    return null;
  }
}
